import { SwankAuthenticationService } from '@swank/authentication';
import { Injectable } from '@angular/core';
import { take, map, shareReplay, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { User, UserClaims, UserPermissions } from './../../models';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  // getUser() has many consumers (including one per rendered order row), so map the
  // claims to a User once and replay the result instead of re-running the
  // permission mapping for every subscriber
  private user$: Observable<User>;

  constructor(private readonly authService: SwankAuthenticationService) { }

  public getUser(): Observable<User> {
    if (!this.user$) {
      this.user$ = this.buildUser().pipe(
        shareReplay(1),
        catchError((error) => {
          this.user$ = null;
          return throwError(() => error);
        })
      );
    }

    return this.user$;
  }

  private buildUser(): Observable<User> {
    const permissions: UserPermissions = {
      insightEditor: false,
      insightOperations: false,
      insightSales: false
    };

    return this.authService.getUserClaimsStream().pipe(
      take(1),
      map(claims => {
        const userClaims = claims as UserClaims;
        if (userClaims && userClaims.roles) {
          userClaims.roles.forEach(role => {
            Object.keys(permissions).forEach(key => {
              const roleName = (role as string);
              if (roleName.toLocaleLowerCase() === key.toLocaleLowerCase()) {
                permissions[key] = true;
              }
            });
          });
        }
        const user: User = {
          name: claims ? claims.name : '',
          accountName: claims ? claims.preferred_username : '',
          permissions,
          isAuthenticated: this.authService.hasValidAccessToken() && this.authService.hasValidIdToken()
        };

        return user;
      })
    );
  }

  public signOut(): void {
    this.authService.logOut(true);
  }
}
